/**
 * #474: bounded ack-watch after a successful group/text hand-off to WhatsApp.
 *
 * A send is logged `sent_pending_ack` at ack 0. The only thing that upgrades it is the live
 * `change:ack` push; if the tab dies, or WhatsApp errors the message (ack -1), the server is left
 * with an ambiguous `sent_pending_ack`. This watch polls the SAME message's store ack for a short
 * window and says what it saw. It is observe-only: it never resends and adds no send path.
 *
 *  - ack >= SERVER: promote (idempotent; the BE ack merge is monotonic).
 *  - ack -1: log row with the observed WA reject tokens.
 *  - still 0 / not in store at the timeout: log row so the server can tell "unacked in a live tab".
 */

import { ACK } from '@bt-common/schemas/wa/message-ack';

export const ACK_WATCH_TIMEOUT_MS = 20_000;
export const ACK_WATCH_INTERVAL_MS = 2_000;
/** Look-back when asking the page for reject tokens: covers the send job that ran just before the hand-off returned. */
export const ACK_WATCH_HANDOFF_MARGIN_MS = 30_000;

export type AckWatchOutcome = 'acked' | 'rejected' | 'unacked';

export interface AckWatchDeps {
    /** Store ack for the message; null when it is not in the store. */
    readAck: () => Promise<number | null>;
    promote: (messageId: string, ack: number) => Promise<unknown>;
    /** Writes one attempt-log row with the given text. */
    report: (messageId: string, logMessage: string) => Promise<unknown>;
    /** Safe reject-reason tokens observed for this send, if any. */
    readReason: () => Promise<string | undefined>;
    sleep: (ms: number) => Promise<void>;
    now: () => number;
}

async function safely(step: () => Promise<unknown>): Promise<void> {
    try {
        await step();
    } catch (err) {
        console.warn('[ack-watch] step failed (non-fatal):', err);
    }
}

/** Never throws. Resolves with what was observed. */
export async function watchSendAck(
    messageId: string,
    waMessageKey: string,
    deps: AckWatchDeps,
    timeoutMs: number = ACK_WATCH_TIMEOUT_MS,
    intervalMs: number = ACK_WATCH_INTERVAL_MS,
): Promise<AckWatchOutcome> {
    const deadline = deps.now() + timeoutMs;
    let sawRow = false;
    let sawReadError = false;
    while (true) {
        let ack: number | null = null;
        try {
            ack = await deps.readAck();
        } catch (err) {
            sawReadError = true;
            console.warn('[ack-watch] ack read failed (non-fatal):', err);
        }
        if (ack !== null) sawRow = true;
        if (ack !== null && ack >= ACK.SERVER) {
            const observed: number = ack;
            await safely(() => deps.promote(messageId, observed));
            return 'acked';
        }
        if (ack === ACK.ERROR) {
            let reason: string | undefined;
            try {
                reason = await deps.readReason();
            } catch {
                reason = undefined;
            }
            await safely(() => deps.report(messageId, `Ack watch: WhatsApp rejected the send after hand-off (ack -1) reason=${reason ?? 'unknown'}`));
            return 'rejected';
        }
        if (deps.now() >= deadline) break;
        await deps.sleep(intervalMs);
    }
    const where = sawRow ? 'ack 0' : sawReadError ? 'ack read failed' : 'not in store';
    await safely(() => deps.report(messageId, `Ack watch: still unacked after ${Math.round(timeoutMs / 1000)}s (${where}); key=${waMessageKey ? 'present' : 'absent'}`));
    return 'unacked';
}
